import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  signal
} from '@angular/core';
import { BaseComponent } from '~app/app/~shared/abstract-components/base.component';
import { Channel, ChannelData } from '~core/models/channel.models';
import { ChannelCreationDialogComponent } from '../../modals/channel-creation-dialog/channel-creation-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { tap, finalize } from 'rxjs';
import {
  MEDIA_TYPES,
  SUPPORTED_IMAGE_EXTENSIONS
} from '~app/app/~shared/constants/global.constants';
import { ChannelService } from '~app/app/~shared/services/channel.service';
import { ResourceFacade } from '~app/app/~store/facades/resource.facade';
import { MediaType } from '~core/models/core.models';
import { ConstantsService } from '~core/services/constants.service';
import { SharedModule } from '~app/app/~shared/modules/shared.module';

@Component({
  selector: 'channel-creation-form',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './channel-creation-form.component.html',
  styleUrl: './channel-creation-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChannelCreationFormComponent
  extends BaseComponent
  implements OnInit
{
  private _constantService = inject(ConstantsService);

  channel = input<Channel | null>(null);
  mediaTypes = MEDIA_TYPES;

  private _channelService = inject(ChannelService);
  private _resourceFacade = inject(ResourceFacade);
  private _dialogRef = inject(MatDialogRef<ChannelCreationDialogComponent>);

  countries$ = this._constantService.getCountryListWithDialCode();

  violations$ = this._globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation =>
            this.form.get(violation.propertyPath)?.setErrors({
              httpError: violation.title
            })
          );
        }, 500);
      }
    })
  );

  isSubmitting = signal(false);
  isHovered = signal(false);
  logo = signal<File | undefined>(undefined);
  logoUrl = signal<string>('');

  form = new FormGroup({
    name: new FormControl<string>('', {
      validators: [Validators.required, Validators.min(3)]
    }),
    country: new FormControl<string>('CI', {
      validators: [Validators.required]
    }),
    mediaType: new FormControl<string>('', {
      validators: [Validators.required]
    }),
    logo: new FormControl(null)
  });

  supportedImageExtensions = SUPPORTED_IMAGE_EXTENSIONS;

  ngOnInit(): void {
    const channel = this.channel();

    if (channel) {
      this.logoUrl.set(channel.logoUrl);

      this.form.get('logo')?.clearValidators();

      this.form.patchValue({
        name: channel.name,
        country: channel.country,
        mediaType: channel.mediaType
      });
    }
  }

  onSubmit(): void {
    this.isSubmitting.set(true);
    const channelId = this.channel()?.id;

    const formValue = this.form.value;
    const values = {
      name: formValue.name || '',
      country: formValue.country as string,
      mediaType: formValue.mediaType as MediaType,
      logo: formValue.logo as unknown as File
    };

    if (channelId) {
      this.updateChannel(channelId, values);
    } else {
      this.createChannel(values);
    }
  }

  createChannel(values: ChannelData): void {
    this._channelService
      .createWithFormData(values)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Support créé avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => {
          this.isSubmitting.set(false);
          this._cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  updateChannel(channelId: string, values: ChannelData): void {
    const { logo, ...rest } = values;
    this._channelService
      .updateWithFormData(channelId, rest)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Support modifié avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  onFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (file) {
      this.setLogo(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isHovered.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();

    this.isHovered.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();

    const fileList = event.dataTransfer?.files;
    this.isHovered.set(false);
    const file = fileList?.[0];

    if (file) {
      this.setLogo(file);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setLogo(logo: any): void {
    this.form.patchValue({ logo });
    this.form.get('logo')?.updateValueAndValidity();
    this.logo.set(logo);

    this.logoUrl.set(URL.createObjectURL(this.logo() as File));
  }
}
